import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useT } from '../i18n';
import { thumbFromDataUrl } from '../utils/imageCompress';
import {
  fetchAnnouncements, saveAnnouncements, announcementLimit, ANNOUNCEMENT_MAX_CHARS,
} from '../firebase/announcementService';
import './AnnouncementEditor.css';

// ── EDITING THE PROFILE BANNER ───────────────────────────────────────────────
// Add, remove and reorder the images a silver or gold designer shows above their
// profile. Silver has one slot, gold has three.
//
// SLOTS, NOT A LIST. The plan's allowance is drawn as that many boxes, filled or
// empty, so the limit is visible before anything is uploaded rather than
// arriving as an error after choosing a file. An empty slot is the invitation;
// a full one is the picture with a remove button.
//
// RENDERS NOTHING BELOW SILVER, so the feature is not advertised to accounts
// that cannot use it. The plans page is where tiers are sold; a disabled control
// on someone's own profile is just clutter they cannot act on.
//
// Re-encoded on the way in — see the note on the size guard below.
const BANNER_MAX_PX = 1600;
const BANNER_QUALITY = 0.82;

export default function AnnouncementEditor({ uid, plan, isShop = false }) {
  const { t } = useT();
  const limit = announcementLimit(plan, isShop);

  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  // CLOSED AT REST. The banner is set once and rarely touched, while the page it
  // sits on is opened constantly — an editor holding three image slots open by
  // default pushes the designer's actual work below the fold every visit.
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');
  const alive = useRef(true);
  const fileRef = useRef(null);

  useEffect(() => {
    alive.current = true;
    if (uid && limit > 0) {
      fetchAnnouncements(uid).then(rows => { if (alive.current) setItems(rows.slice(0, limit)); });
    }
    return () => { alive.current = false; };
  }, [uid, limit]);

  const persist = useCallback(async (next) => {
    setBusy(true);
    setError('');
    try {
      const saved = await saveAnnouncements(uid, plan, next, isShop);
      if (alive.current) setItems(saved);
    } catch (err) {
      if (!alive.current) return;
      // Almost always the rule refusing it, which is worth naming: it means the
      // plan on the server does not allow what the page thinks it does.
      setError(err?.code === 'permission-denied' ? t('ann_err_denied') : t('ann_err_save'));
    } finally {
      if (alive.current) setBusy(false);
    }
  }, [uid, plan, isShop, t]);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';                    // so the same file can be chosen twice
    if (!file) return;
    if (!/^image\//.test(file.type)) { setError(t('ann_err_not_image')); return; }

    setBusy(true);
    setError('');
    try {
      const raw = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.onerror = () => rej(new Error('read'));
        r.readAsDataURL(file);
      });

      // ── RE-ENCODED, ALWAYS ──
      // A phone photograph is several megabytes and would not fit in a
      // Firestore document at all, and even one that fits would be paid for on
      // every view of the profile. Downscaling here is what makes the stored
      // banner a banner rather than a camera original.
      // `{ thumbUrl, width, height }` — NOT `{ url, w, h }`. Reading the wrong
      // key here failed every upload with "could not be read", because the
      // guard below saw undefined and could not tell a bad file from a bad
      // assumption. See thumbFromDataUrl in utils/imageCompress.
      const shrunk = await thumbFromDataUrl(raw, BANNER_MAX_PX, BANNER_QUALITY);
      const url = shrunk?.thumbUrl;
      if (!url || typeof url !== 'string') { setError(t('ann_err_read')); return; }

      // The guard the document limit depends on. Refusing here is far better
      // than a write that fails, or worse, one that succeeds and leaves the
      // document too large to read back.
      if (url.length > ANNOUNCEMENT_MAX_CHARS) { setError(t('ann_err_too_big')); return; }

      await persist([...items, { url, w: shrunk.width || 0, h: shrunk.height || 0 }]);
    } catch {
      if (alive.current) setError(t('ann_err_read'));
    } finally {
      if (alive.current) setBusy(false);
    }
  };

  const remove = (i) => persist(items.filter((_, n) => n !== i));

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    persist(next);
  };

  if (limit === 0) return null;

  const slots = Array.from({ length: limit }, (_, i) => items[i] || null);

  return (
    <section className={`ane${open ? ' ane--open' : ''}`}>
      {/* The whole header is the control, not a separate chevron button beside
          it — a 40px target in the corner is worse than a full-width one, and
          there is nothing else in this row to click. */}
      <button
        type="button"
        className="ane-head"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
      >
        <span className="ane-head-text">
          <span className="ane-title">{t('ann_title')}</span>
          <span className="ane-sub">
            {t(limit > 1 ? 'ann_sub_many' : 'ann_sub_one').replace('{n}', limit)}
          </span>
        </span>
        {/* Count on the closed row, so the section says what is in it without
            being opened. */}
        {items.length > 0 && <span className="ane-count">{items.length}/{limit}</span>}
        <svg className="ane-caret" viewBox="0 0 24 24" width="16" height="16" fill="none"
          stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
          aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
      </button>

      {/* THE 0fr → 1fr GRID TRICK. A height transition needs a number to animate
          to, and the content height is unknown — three slots, an error line that
          may or may not be there. A grid row animates from nothing to auto
          without anyone measuring it. */}
      <div className="ane-body">
        <div className="ane-body-inner">
          {error && <p className="ane-error" role="alert">{error}</p>}

          <div className="ane-slots">
        {slots.map((item, i) => (item ? (
          <div className="ane-slot ane-slot--full" key={`f${i}`}>
            <img className="ane-thumb" src={item.url} alt="" />
            <div className="ane-tools">
              {limit > 1 && (
                <>
                  <button type="button" className="ane-tool" disabled={busy || i === 0}
                    onClick={() => move(i, -1)} aria-label={t('ann_move_left')}>←</button>
                  <button type="button" className="ane-tool" disabled={busy || i === items.length - 1}
                    onClick={() => move(i, 1)} aria-label={t('ann_move_right')}>→</button>
                </>
              )}
              <button type="button" className="ane-tool ane-tool--del" disabled={busy}
                onClick={() => remove(i)} aria-label={t('ann_remove')}>✕</button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            className="ane-slot ane-slot--empty"
            key={`e${i}`}
            disabled={busy}
            onClick={() => fileRef.current?.click()}
          >
            <span className="ane-plus" aria-hidden="true">+</span>
            <span className="ane-add">{busy ? t('ann_working') : t('ann_add')}</span>
          </button>
          )))}
          </div>
        </div>
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        onChange={onPick}
        style={{ display: 'none' }}
      />
    </section>
  );
}
